import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { GlobalCSS } from '../design-system/GlobalCSS';
import { ThemeRoot } from '../design-system/ThemeRoot';
import { Spinner } from '../design-system/atoms/Spinner';
import { useAuthStore } from '../domains/auth/authStore';
import { useCompanyRole } from '../domains/auth/companyRole';
import { usePushPermission } from '../domains/push/usePushPermission';
import { installSyncTriggers } from '../domains/sync/outboxProcessor';
import { useSubscription } from '../features/subscription/useSubscription';
import { Login } from '../pages/Login';
import { ForgotPassword } from '../pages/ForgotPassword';
import { ResetPassword } from '../pages/ResetPassword';
import { NotFound } from '../pages/NotFound';
import { InvoicePage } from '../pages/InvoicePage';
import { NotificationOrderRedirect } from '../pages/NotificationOrderRedirect';
import { NotificationUsersRedirect } from '../pages/NotificationUsersRedirect';
import { PrivacyPage, ConsentPage, TermsPage } from '../pages/LegalDocumentPage';
import { AppShell } from '../shared/AppShell';
import { PWAInstallBanner } from '../shared/PWAInstallBanner';
import { CompanySetupGate } from '../shared/CompanySetupGate';
import { AuthBoundary } from './AuthBoundary';
import { getRoleIssueContent } from './roleIssueContent';
import { RoleGuard } from './RoleGuard';
import { RoleFallbackScreen, RoleLoadingState } from './RoleStateScreen';
import { SuperAdminGuard } from './SuperAdminGuard';

const TechApp = lazy(() => import('../features/technician/TechApp').then((module) => ({ default: module.TechApp })));
const DispApp = lazy(() => import('../features/dispatcher/DispApp').then((module) => ({ default: module.DispApp })));
const AdminPanel = lazy(() => import('../features/admin/screens/AdminPanel').then((module) => ({ default: module.AdminPanel })));
const SuperAdminPanel = lazy(() =>
  import('../features/superadmin/screens/SuperAdminPanel').then((module) => ({ default: module.SuperAdminPanel })),
);

function PushInner() {
  usePushPermission();
  return null;
}

function PushInit() {
  const { hasFeature, loading } = useSubscription();

  if (loading || !hasFeature('push_notifications')) {
    return null;
  }

  return <PushInner />;
}

export function App() {
  useEffect(() => {
    const cleanup = installSyncTriggers();
    return cleanup;
  }, []);

  return (
    <BrowserRouter>
      <GlobalCSS />
      <ThemeRoot>
      <PushInit />
      <PWAInstallBanner />
      <CompanySetupGate />

      <AppShell>
        <Suspense fallback={<Spinner label="Загружаем рабочую область..." />}>
          <RecoveryGuard />
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* 1.30.0 / P0-4 — публичные маршруты восстановления пароля.
                Сознательно вне AuthBoundary: на /reset-password юзер
                приходит по ссылке из письма с recovery-токеном в hash,
                в этот момент полноценной user-сессии в нашем store нет
                (см. authStore.bindAuthListener — PASSWORD_RECOVERY
                ставит recoveryActive=true и НЕ записывает user).
                Удержание юзера на /reset-password при recoveryActive
                делается в RecoveryGuard ниже. */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invoice/:invoiceId" element={<InvoicePage />} />
            {/* 1.34.0 — Юридические документы. Публичный доступ, без auth.
                Нужно чтобы любой мог открыть Политику ПДн до регистрации,
                и чтобы со страницы согласия в LegalConsentGate можно было
                перейти к полному тексту документа. */}
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/consent" element={<ConsentPage />} />
            <Route path="/legal/terms"   element={<TermsPage />} />
            <Route element={<AuthBoundary />}>
              {/* 1.32.0 — единая точка входа для push-уведомлений.
                  Push шлёт сюда независимо от роли получателя, мы сами
                  определяем нужный экран по роли и редиректим. */}
              <Route path="/n/order/:id" element={<NotificationOrderRedirect />} />
              {/* 1.34.0 — точка входа для push о юзерах (запрос на удаление). */}
              <Route path="/n/users" element={<NotificationUsersRedirect />} />
              <Route
                path="/tech/*"
                element={
                  <RoleGuard allowed={['technician']}>
                    <TechApp />
                  </RoleGuard>
                }
              />
              <Route
                path="/dispatch/*"
                element={
                  <RoleGuard allowed={['dispatcher']}>
                    <DispApp />
                  </RoleGuard>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <RoleGuard allowed={['company_admin']}>
                    <AdminPanel />
                  </RoleGuard>
                }
              />
              <Route
                path="/staff"
                element={
                  <RoleGuard allowed={['company_admin']}>
                    <Navigate to="/admin/users" replace />
                  </RoleGuard>
                }
              />
              <Route
                path="/staff/:memberId"
                element={
                  <RoleGuard allowed={['company_admin']}>
                    <StaffMemberLegacyRedirect />
                  </RoleGuard>
                }
              />
              <Route
                path="/superadmin/*"
                element={
                  <SuperAdminGuard>
                    <SuperAdminPanel />
                  </SuperAdminGuard>
                }
              />
              <Route path="/" element={<RoleRedirect />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AppShell>
      </ThemeRoot>
    </BrowserRouter>
  );
}

function StaffMemberLegacyRedirect() {
  const { memberId } = useParams<{ memberId: string }>();
  return <Navigate to={`/admin/users/${memberId ?? ''}`} replace />;
}

function RoleRedirect() {
  const logout = useAuthStore((state) => state.logout);
  const { user, loading, isPlatformUser, missingMembership, unknownRole, defaultRoute } = useCompanyRole();

  if (!user) return <Navigate to="/login" replace />;
  if (loading) return <RoleLoadingState label="Определяем рабочую область..." />;
  if (isPlatformUser) return <Navigate to="/superadmin" replace />;

  if (missingMembership) {
    const issue = getRoleIssueContent('missingMembership', 'redirect');
    return (
      <RoleFallbackScreen
        icon={issue.icon}
        title={issue.title}
        text={issue.text}
        actionLabel="Выйти"
        onAction={() => logout()}
      />
    );
  }

  if (unknownRole) {
    const issue = getRoleIssueContent('unknownRole', 'redirect');
    return (
      <RoleFallbackScreen
        icon={issue.icon}
        title={issue.title}
        text={issue.text}
        actionLabel="Выйти"
        onAction={() => logout()}
      />
    );
  }

  return <Navigate to={defaultRoute} replace />;
}

// 1.30.0 / P0-4 — Глобальный гард recovery-сессии.
//
// Срабатывает поверх всего дерева роутов: если authStore.recoveryActive
// === true (Supabase эмитнул PASSWORD_RECOVERY), и юзер находится не на
// /reset-password, мы программно (SPA-нав, не reload) уводим его туда.
//
// Зачем именно глобально, а не только в AuthBoundary:
// - Recovery-сессия Supabase-ом неотличима от обычной после reload.
//   После смены пароля и SIGNED_OUT флаг снимается, и юзер свободно
//   ходит по публичным маршрутам.
// - Если юзер кликнул по ссылке из письма повторно (например, на другом
//   устройстве) и попал на /login или /forgot-password, без этого гарда
//   он мог бы там «застрять», не понимая, что от него хотят.
//
// SPA-навигация (useNavigate, не window.location.replace) намеренна:
// recovery-сессия живёт в Supabase-клиенте текущей вкладки, а reload
// сбросил бы какие-то промежуточные in-memory вещи (например,
// onAuthStateChange-подписку в ResetPassword.tsx, которая ловит
// поздний эмит сессии).
function RecoveryGuard() {
  const recoveryActive = useAuthStore((state) => state.recoveryActive);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (recoveryActive && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [recoveryActive, location.pathname, navigate]);

  return null;
}
